import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";

import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

import "@google/model-viewer";

import * as Api from "../../api";

import Container from "../../components/Container";
import Loader from "../../components/Loader";
import AlertMsg from "../../components/AlertMsg";
import RestaurantHeader from "../../components/RestaurantHeader";
import AppDialog from "../../components/AppDialog";
import {
  ACCEPTED_IMAGE_TYPES,
  MAX_IMAGE_SIZE,
  MAX_IMAGES_PER_UPLOAD,
} from "../../components/modelImages.constants";

const CreateArModel = () => {
  const navigate = useNavigate();
  const restaurantId = useParams().rest_id;

  const [loadingDishes, setLoadingDishes] = useState(true);
  const [dishes, setDishes] = useState([]); // menu items without a model
  const [dishId, setDishId] = useState("");
  const [selected, setSelected] = useState([]); // { id, file, url }
  const [chosen, setChosen] = useState(() => new Set()); // ids marked for deletion
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [creating, setCreating] = useState(false);
  const [msgAlert, setMsgAlert] = useState({
    open: false,
    message: "",
    msgType: "error",
  });

  const notify = (message, msgType) =>
    setMsgAlert({ open: true, message, msgType });

  // --- Load dishes that don't already have a model ----------------------
  useEffect(() => {
    if (!restaurantId) return;
    setLoadingDishes(true);
    const filter =
      "?pageSize=100000&pageNum=1&filter__idRestaurant=" +
      restaurantId +
      "&orderBy=order&orderByDir=asc";
    Api.getMenuList(filter, restaurantId).then((res) => {
      if (res?.success) {
        setDishes((res.data?.rows || []).filter((d) => d.modelId == null));
      } else {
        notify(res?.msg || "Failed to load menu items", "error");
      }
      setLoadingDishes(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  // --- Local image staging (object-URL cleanup) -------------------------
  const selectedRef = useRef([]);
  const idCounter = useRef(0);
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);
  useEffect(
    () => () => selectedRef.current.forEach((s) => URL.revokeObjectURL(s.url)),
    []
  );

  const onDrop = (accepted) => {
    const remaining = MAX_IMAGES_PER_UPLOAD - selected.length;
    const toAdd = accepted
      .slice(0, Math.max(0, remaining))
      .map((file) => ({
        id: `${Date.now()}-${idCounter.current++}`,
        file,
        url: URL.createObjectURL(file),
      }));
    if (accepted.length > toAdd.length) {
      notify(
        `You can add at most ${MAX_IMAGES_PER_UPLOAD} photos per upload`,
        "error"
      );
    }
    if (toAdd.length) setSelected((prev) => [...prev, ...toAdd]);
  };

  const { getRootProps, getInputProps, isDragActive, open: openPicker } =
    useDropzone({
      accept: { "model/gltf-binary": [".glb"] },
      maxFiles: 1,
      multiple: false,
      disabled: creating,
      noClick: true,
      noKeyboard: true,
      onDropAccepted: onDrop,
      onDropRejected: (rej) => {
        const codes = new Set();
        rej.forEach((r) => r.errors.forEach((e) => codes.add(e.code)));
        if (codes.has("file-invalid-type"))
          notify("Only .glb files are allowed", "error");
        else if (codes.has("too-many-files"))
          notify("Only 1 file can be uploaded at a time", "error");
        else notify("File could not be added", "error");
      },
    });

  // --- Selection / bulk delete ------------------------------------------
  const toggleChosen = (id) => {
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAll = () => setChosen(new Set(selected.map((s) => s.id)));
  const clearChosen = () => setChosen(new Set());

  const deleteChosen = () => {
    setSelected((prev) => {
      prev
        .filter((s) => chosen.has(s.id))
        .forEach((s) => URL.revokeObjectURL(s.url));
      return prev.filter((s) => !chosen.has(s.id));
    });
    setChosen(new Set());
    setConfirmDelete(false);
  };

  // --- Create ------------------------------------------------------------
  const onCreate = async () => {
    const dish = dishes.find((d) => d._id === dishId);
    if (!dish || !selected.length) return;
    setCreating(true);

    const name = (dish.name || "model").toLowerCase().split(" ").join("-");
    const createRes = await Api.createArModel(
      JSON.stringify({
        name,
        isPlane: true,
        meta: { restaurantID: restaurantId },
      })
    );
    if (!createRes?.success) {
      notify(createRes?.msg || "Failed to create the model", "error");
      setCreating(false);
      return;
    }

    const model = createRes.data;
    const formData = new FormData();
    formData.append("glb", selected[0].file);
    const uploadRes = await Api.uploadArModel(formData, model._id);
    const glbUploaded = !!uploadRes?.success;

    const updRes = await Api.updateMenu(
      JSON.stringify({ modelId: model._id }),
      dishId,
      restaurantId
    );
    if (!updRes?.success) {
      // Roll back the orphaned model so a retry doesn't create duplicates.
      await Api.deleteArModel(model._id);
      notify(
        updRes?.msg || "Couldn't link the model to this item. Please try again.",
        "error"
      );
      setCreating(false);
      return;
    }

    selected.forEach((s) => URL.revokeObjectURL(s.url));
    notify(
      glbUploaded
        ? "3D model created successfully."
        : "Model created, but the .glb file didn't upload — please try again.",
      glbUploaded ? "success" : "error"
    );
    navigate("/restaurant/" + restaurantId + "/model/" + dishId);
  };

  const canCreate = !!dishId && selected.length > 0 && !creating;

  return (
    <Container page={"restaurants"} searchVal={""} onSearch={() => null} searchLabel="">
      {loadingDishes ? (
        <Loader height={{ height: window.innerHeight - 150 }} />
      ) : (
        <Grid container item xs={12}>
          <Grid item xs={12} mb={2}>
            <RestaurantHeader
              title={"Add 3D Model"}
              showTitleOnly
              onBackBtnClick={() =>
                navigate("/restaurant/" + restaurantId + "/ar_models")
              }
            />
          </Grid>

          <Grid item xs={12} sx={sx.card} p={3}>
            {dishes.length === 0 ? (
              <Box sx={sx.noDishes}>
                <Typography sx={sx.noDishesTitle}>
                  No menu items available
                </Typography>
                <Typography sx={sx.noDishesText}>
                  An AR model attaches to a dish. Add a menu item first (or every
                  dish already has a model).
                </Typography>
                <Button
                  variant="contained"
                  sx={sx.primaryBtn}
                  onClick={() =>
                    navigate("/restaurant/" + restaurantId + "/manage_menu")
                  }
                >
                  Go to Menu
                </Button>
              </Box>
            ) : (
              <Box sx={{ width: "100%", maxWidth: 880, mx: "auto" }}>
                {/* Dish picker */}
                <Typography sx={sx.label}>Menu item</Typography>
                <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                  <InputLabel>Select a dish</InputLabel>
                  <Select
                    value={dishId}
                    label="Select a dish"
                    onChange={(e) => setDishId(e.target.value)}
                  >
                    {dishes.map((d) => (
                      <MenuItem key={d._id} value={d._id}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Uploader / Preview */}
                <Typography sx={sx.label}>3D Model File</Typography>
                {selected.length === 0 ? (
                  <Box
                    {...getRootProps()}
                    sx={{
                      ...sx.dropzone,
                      borderColor: isDragActive ? "#7CB342" : "#CFE0BE",
                      backgroundColor: isDragActive ? "#F1F8E9" : "#FAFCF7",
                      cursor: creating ? "default" : "pointer",
                      opacity: creating ? 0.7 : 1,
                    }}
                    onClick={creating ? undefined : openPicker}
                  >
                    <input {...getInputProps()} />
                    <CloudUploadOutlinedIcon sx={{ fontSize: 38, color: "#7CB342" }} />
                    <Typography sx={sx.dropTitle}>
                      {isDragActive
                        ? "Drop .glb file here"
                        : "Drag & drop .glb file here, or click to browse"}
                    </Typography>
                    <Typography sx={sx.dropHint}>
                      GLB format only · 1 file
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={sx.previewBox}>
                    <model-viewer
                      src={selected[0].url}
                      style={{ width: "100%", height: "100%" }}
                      camera-controls
                      auto-rotate
                      ar
                      alt={selected[0].file?.name || "3D model preview"}
                    />
                    {!creating && (
                      <Button
                        size="small"
                        sx={sx.removeBtn}
                        onClick={() => {
                          URL.revokeObjectURL(selected[0].url);
                          setSelected([]);
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                )}

                {/* Actions */}
                <Box sx={sx.actions}>
                  <Button
                    onClick={() =>
                      navigate("/restaurant/" + restaurantId + "/ar_models")
                    }
                    sx={sx.cancelBtn}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!canCreate}
                    onClick={onCreate}
                    startIcon={
                      creating ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : null
                    }
                    sx={sx.primaryBtn}
                  >
                    {creating ? "Creating…" : "Create AR Model"}
                  </Button>
                </Box>
              </Box>
            )}
          </Grid>

          <AppDialog
            open={confirmDelete}
            onClose={() => setConfirmDelete(false)}
            title="Remove selected photos?"
            content={`${chosen.size} photo${
              chosen.size === 1 ? "" : "s"
            } will be removed from this upload.`}
            firstBtnContent="Cancel"
            secondBtnContent="Remove"
            firstBtnClickHandler={() => setConfirmDelete(false)}
            secondBtnClickHandler={deleteChosen}
            options={{ keepMounted: false, btnLoading: false }}
          />
          <AlertMsg
            msgAlert={msgAlert}
            onCloseAlertMsg={() =>
              setMsgAlert({ open: false, message: "", msgType: "error" })
            }
          />
        </Grid>
      )}
    </Container>
  );
};

export default CreateArModel;

const sx = {
  card: {
    backgroundColor: "#fff",
    borderRadius: 2,
    border: "1px solid #E3E9DD",
    minHeight: "calc(100vh - 200px)",
  },
  label: {
    fontFamily: "GilroySemiBold",
    fontSize: 13,
    color: "#5B6B52",
    mb: 1,
  },
  dropzone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: 1,
    border: "1.5px dashed",
    borderRadius: 3,
    px: 2,
    py: 5,
    transition: "background-color .15s ease, border-color .15s ease",
  },
  dropTitle: { fontFamily: "GilroySemiBold", fontSize: 14, color: "#3F4B38" },
  dropHint: { fontFamily: "GilroyRegular", fontSize: 12, color: "#8A9881" },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mt: 3,
    mb: 0.5,
  },
  count: { fontFamily: "GilroySemiBold", fontSize: 13, color: "#5B6B52" },
  textBtn: {
    fontFamily: "GilroySemiBold",
    fontSize: 13,
    textTransform: "none",
    color: "#558B2F",
  },
  deleteBtn: {
    fontFamily: "GilroySemiBold",
    fontSize: 13,
    textTransform: "none",
    color: "#fff",
    backgroundColor: "#C62828",
    px: 1.5,
    "&:hover": { backgroundColor: "#A21C1C" },
  },
  thumb: {
    position: "relative",
    width: "100%",
    paddingTop: "100%",
    borderRadius: 2,
    overflow: "hidden",
    border: "2px solid",
    cursor: "pointer",
    backgroundColor: "#F4F7F2",
    transition: "box-shadow .12s ease, border-color .12s ease",
  },
  thumbImg: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  checkBadge: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 24,
    height: 24,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.25)",
  },
  previewBox: {
    position: "relative",
    width: "100%",
    height: 360,
    border: "1.5px solid #CFE0BE",
    borderRadius: 3,
    overflow: "hidden",
    backgroundColor: "#F4F7F2",
  },
  removeBtn: {
    position: "absolute",
    top: 8,
    right: 8,
    fontFamily: "GilroySemiBold",
    fontSize: 12,
    textTransform: "none",
    color: "#fff !important",
    backgroundColor: "rgba(0,0,0,0.5)",
    px: 1.5,
    "&:hover": { backgroundColor: "rgba(198,40,40,0.85)" },
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 2,
    mt: 4,
  },
  cancelBtn: {
    fontFamily: "GilroySemiBold",
    textTransform: "none",
    color: "#5B6B52",
    border: "1px solid #DDE4D6",
    borderRadius: 2,
    px: 3,
  },
  primaryBtn: {
    backgroundColor: "#000000",
    color: "#fff !important",
    fontFamily: "GilroySemiBold",
    textTransform: "none",
    borderRadius: 2,
    px: 3,
    "&:hover": { backgroundColor: "#222222" },
    "&.Mui-disabled": { backgroundColor: "#CCCCCC", color: "#fff !important" },
  },
  noDishes: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: 1,
    py: 8,
  },
  noDishesTitle: { fontFamily: "GilroyBold", fontSize: 18, color: "#1F2A1A" },
  noDishesText: {
    fontFamily: "GilroyRegular",
    fontSize: 13,
    color: "#7A8A72",
    maxWidth: 360,
    mb: 1,
  },
};
